import React, { useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  InputNumber,
  Button,
  Space,
  Spin,
  message,
  Divider,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import { UAE_EMIRATES, UAE_PAYMENT_MEANS, LEGAL_REGISTRATION_TYPES } from '@/constants/uaeEinvoice';
import { PaymentTermSelect } from '@/components/finance/PaymentTermSelect';

const { Title } = Typography;
const { TextArea } = Input;

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

interface CustomerRow {
  id: string;
  customer_code: string;
  customer_name: string;
  customer_type: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  billing_address_line1?: string;
  billing_city?: string;
  billing_country?: string;
  shipping_address_line1?: string;
  shipping_city?: string;
  shipping_country?: string;
  shipping_state?: string;
  tax_registration_number?: string;
  emirate_code?: string;
  billing_state?: string;
  electronic_address?: string;
  electronic_identifier?: string;
  electronic_identifier_scheme?: string;
  legal_registration_number?: string;
  legal_registration_type?: string;
  payment_means_code?: string;
  tax_scheme_code?: string;
  payment_terms?: string;
  credit_limit?: number;
  credit_days?: number;
  is_active?: boolean;
}

function listBasePath(pathname: string): string {
  return pathname.startsWith('/platform') ? '/platform/foundation/customers' : '/growth/customers';
}

function apiTypeToForm(t: string): string {
  if (t === 'Company') return 'Corporate';
  return t === 'Government' ? 'Government' : 'Individual';
}

function formTypeToApi(t: string): string {
  if (t === 'Corporate') return 'Company';
  return t;
}

interface CountryRow {
  id: string;
  iso_code: string;
  name: string;
}

const DEFAULT_COUNTRY = 'United Arab Emirates';
const UAE_ALIASES = new Set(['UAE', 'U.A.E', 'U.A.E.']);

/** Map a stored country (legacy "UAE", ISO code, or name) onto a countries-master name. */
function resolveCountryName(raw: string | null | undefined, countries: CountryRow[]): string | undefined {
  const v = String(raw ?? '').trim();
  if (!v) return undefined;
  if (UAE_ALIASES.has(v.toUpperCase())) return DEFAULT_COUNTRY;
  const upper = v.toUpperCase();
  const match = countries.find((c) => c.name.toUpperCase() === upper || c.iso_code.toUpperCase() === upper);
  return match ? match.name : v;
}

const CUSTOMERS_QUERY_KEY = ['sales-customers'];
const CUSTOMERS_STATS_KEY = ['sales-customers-statistics'];

const CustomerFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const listPath = listBasePath(location.pathname);
  const isEdit = Boolean(id);

  const { data: customer, isLoading } = useQuery({
    queryKey: ['sales-customer', id],
    queryFn: async () => {
      const res = await api.get(`/sales/customers/${id}`);
      return unwrap<CustomerRow>(res);
    },
    enabled: isEdit && !!id,
  });

  const { data: countries = [], isLoading: countriesLoading } = useQuery({
    queryKey: ['procurement-countries'],
    queryFn: async () => {
      const res = await api.get('/procurement/countries');
      const rows = unwrap<CountryRow[]>(res);
      return Array.isArray(rows) ? rows : [];
    },
  });

  const countryOptions = React.useMemo(() => {
    const options = countries.map((c) => ({ value: c.name, label: c.name }));
    for (const raw of [customer?.billing_country, customer?.shipping_country]) {
      const name = resolveCountryName(raw, countries);
      if (name && !options.some((o) => o.value === name)) {
        options.push({ value: name, label: name });
      }
    }
    return options;
  }, [countries, customer?.billing_country, customer?.shipping_country]);

  useEffect(() => {
    if (!isEdit || !customer) return;
    form.setFieldsValue({
      customerName: customer.customer_name,
      customerCode: customer.customer_code,
      contactPerson: customer.contact_person,
      email: customer.email,
      phone: customer.phone,
      city: customer.billing_city,
      country: resolveCountryName(customer.billing_country, countries) || DEFAULT_COUNTRY,
      address: customer.billing_address_line1,
      taxRegistration: customer.tax_registration_number,
      emirateCode: customer.emirate_code || customer.billing_state,
      shippingAddress: customer.shipping_address_line1,
      shippingCity: customer.shipping_city,
      shippingCountry: resolveCountryName(customer.shipping_country, countries) || DEFAULT_COUNTRY,
      shippingEmirateCode: customer.shipping_state,
      electronicAddress: customer.electronic_address,
      electronicIdentifier: customer.electronic_identifier,
      legalRegistrationNumber: customer.legal_registration_number,
      legalRegistrationType: customer.legal_registration_type || 'TL',
      paymentMeansCode: customer.payment_means_code || '30',
      customerType: apiTypeToForm(customer.customer_type || 'Individual'),
      paymentTerms: customer.payment_terms || 'Net 30',
      creditLimit: Number(customer.credit_limit) || 0,
      creditDays: customer.credit_days ?? 30,
      status: customer.is_active ? 'Active' : 'Inactive',
    });
  }, [isEdit, customer?.id]);

  useEffect(() => {
    if (countries.length === 0) return;
    const current = form.getFieldsValue(['country', 'shippingCountry']);
    form.setFieldsValue({
      country: resolveCountryName(current.country, countries) || DEFAULT_COUNTRY,
      shippingCountry: resolveCountryName(current.shippingCountry, countries) || DEFAULT_COUNTRY,
    });
  }, [countries, form]);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        country: DEFAULT_COUNTRY,
        customerType: 'Corporate',
        paymentTerms: 'Net 30',
        creditLimit: 0,
        creditDays: 30,
        status: 'Active',
      });
    }
  }, [isEdit, form]);

  const goBack = () => navigate(listPath);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: Record<string, unknown> = {
        customer_name: values.customerName,
        contact_person: values.contactPerson,
        email: values.email,
        phone: values.phone,
        billing_city: values.city,
        billing_country: values.country,
        billing_address_line1: values.address,
        billing_state: values.emirateCode,
        emirate_code: values.emirateCode,
        shipping_address_line1: values.shippingAddress || null,
        shipping_city: values.shippingCity || null,
        shipping_country: values.shippingCountry || null,
        shipping_state: values.shippingEmirateCode || null,
        tax_registration_number: values.taxRegistration,
        electronic_address: values.electronicAddress,
        electronic_identifier: values.electronicIdentifier,
        legal_registration_number: values.legalRegistrationNumber,
        legal_registration_type: values.legalRegistrationType,
        payment_means_code: values.paymentMeansCode,
        tax_scheme_code: 'VAT',
        customer_type: formTypeToApi(values.customerType),
        payment_terms: values.paymentTerms,
        credit_limit: values.creditLimit ?? 0,
        credit_days: values.creditDays ?? 30,
        is_active: values.status === 'Active',
      };
      if (values.customerCode) {
        payload.customer_code = values.customerCode;
      }
      if (isEdit && id) {
        await api.put(`/sales/customers/${id}`, payload);
        message.success('Customer updated');
      } else {
        await api.post('/sales/customers', payload);
        message.success('Customer created');
      }
      await queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: CUSTOMERS_STATS_KEY });
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      navigate(listPath);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || error?.message || 'Save failed');
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
          Back
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {isEdit ? 'Edit customer' : 'New customer'}
        </Title>
      </Space>

      <Card>
        <Spin spinning={isEdit && isLoading}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="customerName"
                  label="Customer name"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder="Legal name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="customerCode" label="Customer code (optional)">
                  <Input placeholder="Auto-generated if empty" disabled={isEdit} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="contactPerson" label="Contact person" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ required: true }, { type: 'email', message: 'Invalid email' }]}
                >
                  <Input prefix={<MailOutlined />} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
                  <Input prefix={<PhoneOutlined />} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="city" label="City" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="country" label="Country" rules={[{ required: true }]} initialValue={DEFAULT_COUNTRY}>
                  <Select showSearch optionFilterProp="label" loading={countriesLoading} options={countryOptions} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="taxRegistration" label="Tax registration (TRN)">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="address" label="Billing address" rules={[{ required: true, message: 'Required for UAE e-invoice' }]}>
              <TextArea rows={2} />
            </Form.Item>

            <Divider orientation="left">Shipping address</Divider>
            <Form.Item name="shippingAddress" label="Shipping address">
              <TextArea rows={2} placeholder="Ship-to / delivery location" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="shippingCity" label="Shipping city">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="shippingCountry" label="Shipping country" initialValue={DEFAULT_COUNTRY}>
                  <Select showSearch optionFilterProp="label" loading={countriesLoading} options={countryOptions} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="shippingEmirateCode" label="Shipping emirate">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={UAE_EMIRATES}
                    placeholder="Optional"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">UAE E-Invoice (buyer)</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="emirateCode" label="Emirate" rules={[{ required: true, message: 'Required for UAE e-invoice' }]}>
                  <Select showSearch optionFilterProp="label" options={UAE_EMIRATES} placeholder="Select emirate" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="paymentMeansCode" label="Payment means code">
                  <Select options={UAE_PAYMENT_MEANS} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="electronicAddress" label="Electronic address (Peppol / email)">
                  <Input placeholder="email@company.ae or Peppol ID" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="electronicIdentifier" label="Electronic identifier">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="legalRegistrationNumber" label="Legal registration #">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="legalRegistrationType" label="Legal registration type">
                  <Select options={LEGAL_REGISTRATION_TYPES} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="customerType" label="Customer type" rules={[{ required: true }]}>
                  <Select showSearch optionFilterProp="children">
                    <Select.Option value="Corporate">Corporate</Select.Option>
                    <Select.Option value="Individual">Individual</Select.Option>
                    <Select.Option value="Government">Government</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="paymentTerms" label="Payment terms" rules={[{ required: true }]}>
                  <PaymentTermSelect
                    storeAs="name"
                    onTermSelected={(term) => {
                      if (term) form.setFieldsValue({ creditDays: Number(term.days) || 0 });
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="creditLimit" label="Credit limit" initialValue={0}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="creditDays" label="Credit days" initialValue={30}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="status" label="Status" initialValue="Active">
              <Select showSearch optionFilterProp="children">
                <Select.Option value="Active">Active</Select.Option>
                <Select.Option value="Inactive">Inactive</Select.Option>
              </Select>
            </Form.Item>

            <Space>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                Save
              </Button>
              <Button onClick={goBack}>Cancel</Button>
            </Space>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

export default CustomerFormPage;
